/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import type { DiagLogger } from "@opentelemetry/api";
import { hrTime, hrTimeToMilliseconds } from "@opentelemetry/core";

import { LoggingDestination, LoggingFlags } from "./Types";
import { getConfiguration } from "./configuration/StartupConfig";
import { verboseFormatException } from "./utils/LoggingUtils";
import { logStartupBanner } from "./utils/StartupBanner";
import { getThreadId } from "./utils/ThreadId";

// ============================================================================

export enum LogKind {
	Info =  "info   ",
	Warn =  "warning",
	Error = "error  ",
	Debug = "debug  "
}

export class DtLogger {
	/**
	 * Returns true if logger is enabled.
	 * Optionally the logging flag is checked
	 * @param flag Logging flag to check
	 * @returns true if log message will be sent out
	 */
	public enabled(flag?: string): boolean {
		return this._logFn != null && ((flag == null) ? true : (this._flags[flag] === true));
	}

	/**
	 * Configure the logger.
	 * @param destination The logging destination to use
	 * @param flags The logging flags
	 */
	public configure(destination = LoggingDestination.Off, flags: LoggingFlags = Object.create(null)): void {
		switch (destination) {
			case LoggingDestination.StdErr:
				// eslint-disable-next-line no-console
				this._logFn = console.error;
				break;
			case LoggingDestination.StdOut:
				// eslint-disable-next-line no-console
				this._logFn = console.log;
				break;
			default:
				this._logFn = undefined;
				break;
		}
		this._flags = flags;
	}

	/**
	 * Formats and sends a log message.
	 * @param kind The kind of message (e.g. error/warning)
	 * @param component The component logging the message
	 * @param msg The actual message
	 */
	public log(kind: LogKind, component: string, msg: string): void {
		if (this._logFn == null) {
			return;
		}

		// ISO format is yyyy-MM-DDTHH:MM:SS.sssZ
		// our format is yyyy-MM-DD HH:MM:SS.sss UTC
		const date = `${new Date(hrTimeToMilliseconds(hrTime())).toISOString().replace(/(T|Z$)/g, " ")}UTC`;
		const message = `[Dynatrace] ${date} [${process.pid}-${getThreadId()}] ${kind} [${component}] ${msg}`;

		this._logFn(message);
	}

	/**
	 * Helper to log an info message.
	 * @see log
	 */
	public info  = (component: string, msg: string): void => this.log(LogKind.Info,  component, msg);

	/**
	 * Helper to log a warning message.
	 * @see log
	 */
	public warn  = (component: string, msg: string): void => this.log(LogKind.Warn,  component, msg);

	/**
	 * Helper to log an error message.
	 * @see log
	 */
	public error = (component: string, msg: string): void => this.log(LogKind.Error, component, msg);

	/**
	 * Helper to log a debug message.
	 * @see log
	 */
	public debug = (component: string, msg: string): void => this.log(LogKind.Debug, component, msg);

	/**
	 * Helper to log an agent exception as warning.
	 * @param component The component logging the message
	 * @param e the exception to log
	 */
	public logAgentException(component: string, e: unknown): void {
		this.warn(component, `exception in agent code: ${verboseFormatException(e)}`);
	}

	private _flags: LoggingFlags = Object.create(null);
	private _logFn?: (msg: string) => void;
}

// ============================================================================

/**
 * elementary logging interface
 */
export interface Logger {
	/**
	 * true, if logging is enabled
	 */
	readonly enabled: boolean;

	/**
	 * true, if debug logging is enabled
	 */
	readonly debugEnabled: boolean;

	debug(msg: string): void;
	info(msg: string): void;
	warn(msg: string): void;
	error(msg: string): void;

	/**
	 * unified reporting of exceptions caught in agent code
	 * @param e
	 */
	logAgentException(e: unknown): void;
}

// ============================================================================

/**
 * convenience class for component level logging
 */
export class ComponentLogger implements Logger, DiagLogger {
	constructor(public readonly componentName: string, debugFlagName?: string) {
		this.debugFlagName = (debugFlagName == null) ? componentName : debugFlagName;
	}

	public readonly debugFlagName: string;

	public get enabled(): boolean {
		return logger.enabled();
	}

	public get debugEnabled(): boolean {
		return logger.enabled(this.debugFlagName);
	}

	public info(msg: string): void {
		logger.info(this.componentName, msg);
	}

	public warn(msg: string): void {
		logger.warn(this.componentName, msg);
	}

	public error(msg: string): void {
		logger.error(this.componentName, msg);
	}

	public verbose(msg: string): void {
		this.debug(msg);
	}

	/**
	 * Helper to log a debug message.
	 * Note: check if debug logging is enabled before calling this API to avoid expensive
	 * log message composition
	 * @see log
	 */
	public debug(msg: string): void {
		if (this.debugEnabled) {
			logger.debug(this.componentName, msg);
		}
	}

	public logAgentException(e: unknown): void {
		logger.logAgentException(this.componentName, e);
	}
}

// now create Logger instance, configure it and log the startup Banner
export const logger = new DtLogger();

const config = getConfiguration();
logger.configure(config.loggingDestination, config.loggingFlags);

logStartupBanner();
